import dayjs from "dayjs";
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../../../consts";
import { getTodayDate } from "./utils";
import {
  updateForecastedPlots,
  updateHarvestedPlots,
  updateSiteMetadata,
} from "../../../../utils/api";

const useSpinachDashboardActions = ({
  currentOrg,
  currentSite,
  currentCrop,
  greenHouseLabel,
  blockLabel,
  siteMetadata,
  greenhouseData,
  summaryForecastData,
  setSummaryForecastData,
  harvestDateEdits,
  setHarvestDateEdits,
  yieldEdits,
  setYieldEdits,
  weightEdits,
  setWeightEdits,
  showForecastWeightColumn,
  setShowYieldInput,
  setShowWeightInput,
  latLong,
  setBulkSaving,
  excelRawData,
  allRowGraphData,
  buildSummaryForecast,
  fetchGreenhouseData,
  messageApi,
  minLeafSizeInput,
  maxLeafSizeInput,
  setMinimumIdealHarvestLeafSize,
  setMaximumIdealHarvestLeafSize,
  setSavingLeafSize,
}) => {
  const handleSaveAllData = async (showMessage = true) => {
    const todayDate = getTodayDate(latLong).format(BACKEND_DATE_FORMAT);
    const forecastedData = {};

    for (const ghName in summaryForecastData) {
      if (!forecastedData[ghName]) {
        forecastedData[ghName] = [];
      }

      for (const row of summaryForecastData[ghName]) {
        // if at least 1 day has date
        const hasAnyForecastDate =
          row?.day1?.date ||
          row?.day2?.date ||
          row?.day3?.date ||
          row?.day4?.date ||
          row?.day5?.date;

        if (hasAnyForecastDate) {
          const dayItems = ["day1", "day2", "day3", "day4", "day5"].map(
            (dKey) => {
              const dVal = row[dKey];
              if (dVal && typeof dVal === "object") {
                return {
                  date: dVal.date
                    ? dayjs(dVal.date, UI_DATE_FORMAT).format(
                        BACKEND_DATE_FORMAT,
                      )
                    : null,
                  average_length: dVal.averageLength,
                  average_percentile80_95: dVal.averagePercentile80_95,
                  harvested_date: row?.harvestDate,
                  last_length: {
                    ...row?.lastData,
                    date: row?.lastData?.date
                      ? dayjs(row?.lastData?.date, UI_DATE_FORMAT).format(
                          BACKEND_DATE_FORMAT,
                        )
                      : null,
                  },
                };
              }
              // string like "Harvested"
              return {
                date: null,
                average_length: dVal,
                average_percentile80_95: dVal,
                harvested_date: row?.harvestDate,
                last_length: {
                  ...row?.lastData,
                  date: row?.lastData?.date
                    ? dayjs(row?.lastData?.date, UI_DATE_FORMAT).format(
                        BACKEND_DATE_FORMAT,
                      )
                    : null,
                },
              };
            },
          );

          // filter out days with null date
          const dayItemsFiltered = dayItems.filter((d) => d.date !== null);
          if (dayItemsFiltered.length > 0) {
            forecastedData[ghName].push({
              plot_name: row.plotName,
              plot_id: row.plotId,
              greenhouse_id: row.greenhouseId,
              forecasts: dayItemsFiltered,
            });
          }
        }
      }
    }

    const response = await updateForecastedPlots(
      currentOrg,
      currentSite.id,
      currentCrop.id,
      todayDate,
      forecastedData,
    );

    if (response.status === 200 && showMessage) {
      messageApi.success("All data saved.");
    } else if (showMessage) {
      messageApi.error("Error saving data.");
    }
  };

  const handleBulkSave = async (ghName) => {
    try {
      setBulkSaving((prev) => ({ ...prev, [ghName]: true }));

      const tableRows = summaryForecastData[ghName] || [];
      const updatedRows = [];
      const rowsToCloseYield = [];
      const rowsToCloseWeight = [];
      const missingDateRows = [];

      const todayStr = getTodayDate(latLong).format(BACKEND_DATE_FORMAT);

      for (const row of tableRows) {
        const key = `${ghName}-${row.plotName}`;
        const harvestEdit = harvestDateEdits[key]?.value;
        const yieldEdit = yieldEdits[key]?.value;
        const weightEdit = weightEdits[key]?.value;

        // nothing changed -> keep as-is
        if (
          harvestEdit === undefined &&
          yieldEdit === undefined &&
          weightEdit === undefined
        ) {
          updatedRows.push(row);
          continue;
        }

        const candidateHarvest = harvestDateEdits[key]?.value
          ? harvestDateEdits[key].value.format(BACKEND_DATE_FORMAT)
          : row.harvestDate || "";

        const newYield =
          (yieldEdits[key]?.value !== undefined
            ? yieldEdits[key].value
            : row.yield) ?? 0;

        const newWeight =
          (weightEdits[key]?.value !== undefined
            ? weightEdits[key].value
            : row.weight) ?? 0;

        // enforce rules:
        // - if Discarded (-1) and no date -> auto today
        // - else (0 or >0) require a date
        let effectiveHarvest = candidateHarvest;
        if (!effectiveHarvest) {
          if (newYield === -1) {
            effectiveHarvest = todayStr;
          } else if (yieldEdit !== undefined || weightEdit !== undefined) {
            // user edited value but no date -> skip and report
            missingDateRows.push(row.plotName);
            updatedRows.push(row);
            continue;
          } else {
            updatedRows.push(row);
            continue;
          }
        }

        if (
          effectiveHarvest !== row.harvestDate ||
          newYield !== row.yield ||
          (showForecastWeightColumn && newWeight !== row.weight)
        ) {
          const resp = await updateHarvestedPlots(
            currentOrg,
            currentSite.id,
            currentCrop.id,
            row.greenhouseId,
            row.plotId,
            effectiveHarvest,
            newYield,
            newWeight,
          );
          if (resp.status !== 200) {
            messageApi.error(
              `Failed saving ${greenHouseLabel} ${ghName} / ${blockLabel} ${row.plotName} (${resp.status})`,
            );
          }
        }

        updatedRows.push({
          ...row,
          harvestDate: effectiveHarvest,
          yield: newYield,
          weight: newWeight,
        });

        if (newYield === 0 || newYield === -1) rowsToCloseYield.push(key);
        if (newWeight === 0 || newWeight === -1) rowsToCloseWeight.push(key);
      }

      /* ---------- 2. update local React state ---------- */
      setSummaryForecastData((prev) => ({ ...prev, [ghName]: updatedRows }));

      setHarvestDateEdits((prev) => {
        const next = { ...prev };
        Object.keys(next)
          .filter((k) => k.startsWith(`${ghName}-`))
          .forEach((k) => delete next[k]);
        return next;
      });
      setYieldEdits((prev) => {
        const next = { ...prev };
        Object.keys(next)
          .filter((k) => k.startsWith(`${ghName}-`))
          .forEach((k) => delete next[k]);
        return next;
      });
      setWeightEdits((prev) => {
        const next = { ...prev };
        Object.keys(next)
          .filter((k) => k.startsWith(`${ghName}-`))
          .forEach((k) => delete next[k]);
        return next;
      });

      // Close any open inputs that are now special (0 or −1)
      if (rowsToCloseYield.length) {
        setShowYieldInput((prev) => {
          const next = { ...prev };
          rowsToCloseYield.forEach((rk) => {
            next[rk] = false;
          });
          return next;
        });
      }
      if (rowsToCloseWeight.length) {
        setShowWeightInput((prev) => {
          const next = { ...prev };
          rowsToCloseWeight.forEach((rk) => {
            next[rk] = false;
          });
          return next;
        });
      }

      await buildSummaryForecast(excelRawData, allRowGraphData, [ghName]);

      /* ---------- 3. persist forecast data ---------- */
      await handleSaveAllData(false);

      if (missingDateRows.length) {
        messageApi.error(
          `Harvest date is required for ${missingDateRows.length} row(s): ${missingDateRows.join(", ")}. Those were skipped.`,
        );
      } else {
        messageApi.success("Data saved successfully.");
      }
    } catch (err) {
      console.error(err);
      messageApi.error(
        err?.message || "Unexpected error while saving. Please retry.",
      );
    } finally {
      setBulkSaving((prev) => ({ ...prev, [ghName]: false }));
    }
  };

  const updateHarvestDate = async ({
    greenhouseId,
    plotId,
    newHarvestDate,
    totalYield = null,
  }) => {
    const response = await updateHarvestedPlots(
      currentOrg,
      currentSite.id,
      currentCrop.id,
      greenhouseId,
      plotId,
      newHarvestDate,
      totalYield,
    );
    if (response.status === 200) {
      messageApi.info("Harvest date updated.");
      return response.data.data;
    } else {
      messageApi.error("Error updating harvest date.");
      return null;
    }
  };

  const handleSaveLeafSize = async () => {
    if (!currentSite || !currentCrop) {
      messageApi.error("Site or crop not selected.");
      return;
    }

    setSavingLeafSize(true);
    try {
      // Create updated metadata
      const updatedMetadata = currentSite?.metadata;
      if (!updatedMetadata[currentCrop.name]) {
        updatedMetadata[currentCrop.name] = {};
      }

      // Update the leaf size values
      updatedMetadata[currentCrop.name].MIN_IDEAL_HARVEST_LEAF_SIZE =
        minLeafSizeInput;
      updatedMetadata[currentCrop.name].MAX_IDEAL_HARVEST_LEAF_SIZE =
        maxLeafSizeInput;

      // Update site with new metadata
      const updateRes = await updateSiteMetadata(currentSite.id, {
        metadata: updatedMetadata,
      });

      if (updateRes.status === 200) {
        // Update state variables
        setMinimumIdealHarvestLeafSize(minLeafSizeInput);
        setMaximumIdealHarvestLeafSize(maxLeafSizeInput);
        messageApi.success("Leaf size values updated successfully.");
      } else {
        messageApi.error("Error updating leaf size values.");
      }
    } catch (error) {
      console.error("Error saving leaf size:", error);
      messageApi.error("Error updating leaf size values.");
    } finally {
      setSavingLeafSize(false);
    }
  };

  return {
    handleSaveAllData,
    handleBulkSave,
    updateHarvestDate,
    handleSaveLeafSize,
  };
};

export default useSpinachDashboardActions;
